<script lang="ts" setup>
import {
  useReplacePresentationHcpsMutation,
  PresentationAssignmentsDocument,
  type PresentationAssignmentsQuery,
  type PresentationAssignmentsQueryVariables,
  type DocumentHistoryQuery,
} from "@/graphql/operations";
import { ref, computed } from "vue";
import { useClientHandle } from "@urql/vue";
import { Dropdown, Modal, ContactPicker, LoadingSpinner } from "@/components";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import LabelButton from "./LabelButton.vue";

type Entry = NonNullable<
  NonNullable<DocumentHistoryQuery["documentHistoryEntries"]>["nodes"][number]
>;
type Label = NonNullable<Entry["label"]>;
type Contact = NonNullable<Label["hcpContacts"]["nodes"][number]>;
const props = defineProps<{
  viewIds: string[];
  label?: Entry["label"];
  assignments: Array<Pick<Entry, "id" | "label">>;
  singleButton?: boolean;
  type?: string;
}>();
const emit = defineEmits(["success", "reload"]);
const { t } = useI18n();
const store = useStore();
const client = useClientHandle().client;
const { executeMutation } = useReplacePresentationHcpsMutation();
const items = computed(() => [
  {
    value: "presentation",
    title: t("documentHistory.label.presentation.title"),
  },
  { value: "self-study", title: t("documentHistory.label.self-study.title") },
]);
const hasCRMIntegration = computed(() =>
  store.getters.user.hasCRMIntegration(),
);
const displayContactPicker = ref(false);
const saving = ref(false);
const loading = ref(false);
const error = ref("");
const snapshot = ref<Array<Pick<Entry, "id" | "label">>>([]);
const selectedContacts = ref<Contact[]>([]);
const mixed = ref(false);
const incomplete = ref(false);
const touched = ref(false);
const pickerKey = ref(0);
const desiredLabel = ref<"presentation" | "self-study">("presentation");
const ready = computed(
  () =>
    snapshot.value.length === props.viewIds.length &&
    snapshot.value.length > 0 &&
    snapshot.value.every((entry) => entry.label?.assignmentRevision),
);
const visibleContacts = computed(
  () =>
    props.label?.hcpContacts.nodes.filter((c): c is Contact => Boolean(c)) ??
    [],
);
const contactSummary = computed(() => {
  const names = visibleContacts.value
    .slice(0, 2)
    .map((c) => c.lastName || c.firstName || t("documentHistory.hcps.contact"));
  const extra = (props.label?.hcpCount ?? 0) - names.length;
  return [names.join(", "), extra > 0 ? `+${extra}` : ""]
    .filter(Boolean)
    .join(" ");
});
function initialize(entries: Array<Pick<Entry, "id" | "label">>) {
  snapshot.value = entries.map((entry) => ({
    id: entry.id,
    label: entry.label ? { ...entry.label } : null,
  }));
  const sets = entries.map(
    (entry) =>
      entry.label?.hcpContacts.nodes.filter((c): c is Contact => Boolean(c)) ??
      [],
  );
  incomplete.value = entries.some(
    (entry, i) => (entry.label?.hcpCount ?? 0) !== sets[i].length,
  );
  const keys = sets.map((contacts) =>
    contacts
      .map((c) => c.id)
      .sort()
      .join(","),
  );
  mixed.value = incomplete.value || keys.some((key) => key !== keys[0]);
  selectedContacts.value = mixed.value ? [] : [...(sets[0] ?? [])];
  touched.value = false;
  pickerKey.value++;
}
function reset() {
  if (saving.value || loading.value) return;
  displayContactPicker.value = false;
  selectedContacts.value = [];
  error.value = "";
}
function openPicker() {
  desiredLabel.value = "presentation";
  initialize(props.assignments);
  error.value = ready.value ? "" : t("documentHistory.hcps.reloadFailed");
  displayContactPicker.value = true;
}
function selectContacts(contacts: Contact[]) {
  if (saving.value || loading.value) return;
  selectedContacts.value = contacts;
  touched.value = true;
}
async function reloadAssignments() {
  loading.value = true;
  try {
    const result = await client
      .query<
        PresentationAssignmentsQuery,
        PresentationAssignmentsQueryVariables
      >(PresentationAssignmentsDocument, { ids: props.viewIds, first: props.viewIds.length }, { requestPolicy: "network-only" })
      .toPromise();
    if (result.error) throw result.error;
    const entries =
      result.data?.documentHistoryEntries?.nodes.filter(
        (entry): entry is NonNullable<typeof entry> => Boolean(entry),
      ) ?? [];
    initialize(entries);
    if (!ready.value) throw new Error("Missing presentation");
    emit("reload");
    return true;
  } catch {
    snapshot.value = [];
    error.value = t("documentHistory.hcps.reloadFailed");
    return false;
  } finally {
    loading.value = false;
  }
}
async function save(labelText: "presentation" | "self-study", clear = false) {
  if (
    saving.value ||
    loading.value ||
    !ready.value ||
    (!clear && mixed.value && !touched.value)
  )
    return;
  saving.value = true;
  error.value = "";
  try {
    const result = await executeMutation({
      input: {
        targets: snapshot.value.map((entry) => ({
          documentViewId: entry.id,
          expectedRevision: entry.label!.assignmentRevision,
        })),
        crmContactIds: clear
          ? []
          : selectedContacts.value.map((contact) => contact.id),
        labelText,
      },
    });
    if (result.error) {
      if (
        result.error.graphQLErrors.some(
          (e) => e.extensions?.code === "PRESENTATION_ASSIGNMENT_CONFLICT",
        )
      ) {
        if (await reloadAssignments())
          error.value = t("documentHistory.hcps.conflict");
      } else error.value = t("documentHistory.hcps.saveFailed");
      return;
    }
    const accepted =
      result.data?.replacePresentationHcps?.labelsOfDocumentViews;
    if (
      !accepted ||
      accepted.length !== snapshot.value.length ||
      snapshot.value.some(
        (entry) =>
          !accepted.some((label) => label?.documentViewId === entry.id),
      )
    ) {
      error.value = t("documentHistory.hcps.saveFailed");
      return;
    }
    displayContactPicker.value = false;
    emit("success");
  } catch {
    error.value = t("documentHistory.hcps.saveFailed");
  } finally {
    saving.value = false;
  }
}
async function handleChange(value: string) {
  if (saving.value || loading.value) return;
  openPicker();
  if (value === "self-study") {
    desiredLabel.value = "self-study";
    await save("self-study", true);
  } else if (!hasCRMIntegration.value) await save("presentation", true);
}
</script>

<template>
  <div class="document_view_label">
    <LabelButton v-if="singleButton" :type="props.type" @click="handleChange" />
    <template v-else>
      <LabelButton
        v-if="label?.hcpCount"
        :type="'contact'"
        @click="openPicker"
        >{{ contactSummary }}</LabelButton
      >
      <LabelButton
        v-else-if="hasCRMIntegration && label?.text === 'presentation'"
        :type="'contact-add'"
        @click="openPicker"
      />
      <Dropdown :items="items" :value="label?.text" @change="handleChange">
        <LabelButton :type="label?.text" :interactive="false">
          <template #right
            ><span class="material-symbols-outlined" aria-hidden="true"
              >more_vert</span
            ></template
          >
        </LabelButton>
      </Dropdown>
    </template>
    <Modal
      class="adaptive_footer"
      v-if="displayContactPicker"
      :active="true"
      :scrolling="true"
      :close-disabled="saving || loading"
      @close="reset"
    >
      <template #header>{{
        viewIds.length > 1
          ? t("documentHistory.hcps.replaceMany", { count: viewIds.length })
          : t("documentHistory.hcps.title")
      }}</template>
      <template #body>
        <div v-if="error" role="alert" class="assignment_message">
          {{ error }}
          <button
            type="button"
            class="button secondary"
            :disabled="saving || loading"
            @click="reloadAssignments"
          >
            {{ t("documentHistory.hcps.reload") }}
          </button>
        </div>
        <p
          v-if="desiredLabel === 'presentation' && mixed && !touched"
          data-test="mixed-assignments"
        >
          {{ t("documentHistory.hcps.mixed") }}
        </p>
        <p v-if="incomplete" role="status">
          {{ t("documentHistory.hcps.incomplete") }}
        </p>
        <LoadingSpinner v-if="loading" />
        <fieldset
          v-else-if="desiredLabel === 'presentation'"
          :disabled="saving"
          class="picker_fieldset"
        >
          <ContactPicker
            :key="pickerKey"
            :space-mode="false"
            :multiple="true"
            :inherit-classic-theme="true"
            :selected-contacts="selectedContacts"
            @selection-change="selectContacts"
          />
        </fieldset>
      </template>
      <template #footer>
        <div class="buttons">
          <button
            type="button"
            class="button secondary"
            :disabled="saving || loading"
            @click="reset"
          >
            {{ t("cancel") }}
          </button>
          <button
            v-if="desiredLabel === 'presentation'"
            type="button"
            class="button secondary"
            :disabled="saving || loading || !ready"
            data-test="save-without-contact"
            @click="save('presentation', true)"
          >
            {{ t("documentHistory.hcps.empty") }}
          </button>
          <button
            v-if="desiredLabel === 'presentation' && selectedContacts.length"
            type="button"
            class="button primary"
            :disabled="saving || loading || !ready || (mixed && !touched)"
            data-test="save-contacts"
            @click="save('presentation')"
          >
            {{
              t("documentHistory.hcps.apply", {
                count: selectedContacts.length,
              })
            }}
          </button>
          <button
            v-if="desiredLabel === 'self-study'"
            type="button"
            class="button primary"
            :disabled="saving || loading || !ready"
            data-test="save-self-study"
            @click="save('self-study', true)"
          >
            {{ t("documentHistory.label.self-study.title") }}
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<style lang="scss" scoped>
.buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
  white-space: normal;
}
.modal .buttons .button {
  margin: 0;
}
.modal :deep(.window) {
  max-width: calc(100vw - 32px);
}
.document_view_label {
  display: flex;
  align-items: center;
}
.dropdown_button + .dropdown {
  margin-left: 0.5em;
}
.modal {
  color: var(--classic-text, initial);
}
.picker_fieldset {
  border: 0;
  padding: 0;
  margin: 0;
  min-width: 0;
}
.assignment_message {
  padding: 0.75em;
  background: var(--classic-warning-surface, #fff3dd);
  margin-bottom: 0.75em;
}
</style>
